"use client"

import React, { useEffect, useState } from "react"
import Link from "next/link"
import { usePathname } from "next/navigation"
import { productivityTools, servicesMenu } from '@/lib/menu-data'
import { trackPostHogEvent } from '@/lib/utils/dataLayer'
import { cn } from '@/lib/utils'
import { marketingSiteOriginFromHost, siteHref } from '@/lib/marketing-hosts'
import { Library } from 'lucide-react'
import DoNotSellLink from '@/components/DoNotSellLink'

export default function Footer({
  variant = 'default',
  className = '',
}: {
  variant?: 'default' | 'onDark'
  className?: string
}) {
  const pathname = usePathname()
  const [siteOrigin, setSiteOrigin] = useState('')
  const toSite = (path: string) => siteHref(path, siteOrigin)
  const showToolsColumns = pathname?.startsWith('/agency-tools') ?? false

  useEffect(() => {
    setSiteOrigin(marketingSiteOriginFromHost(window.location.hostname, window.location.port))
  }, [])
  const onDark = variant === 'onDark'

  const headingClass = onDark
    ? 'text-sm font-bold text-white mb-4 uppercase tracking-wide'
    : 'text-sm font-bold text-gray-900 mb-4 uppercase tracking-wide'
  const linkClass = onDark
    ? 'text-sm text-white/75 hover:text-white transition-colors'
    : 'text-sm text-gray-600 hover:text-[#027dff] transition-colors'
  const brandClass = onDark
    ? 'text-white hover:text-white/80'
    : 'text-gray-900 hover:text-[#027dff]'
  const mutedClass = onDark
    ? 'text-white/75 hover:text-white'
    : 'text-gray-600 hover:text-[#027dff]'
  const dividerClass = onDark ? 'border-t border-white/20 my-2' : 'border-t border-gray-200 my-2'
  const bottomBorderClass = onDark ? 'pt-8 border-t border-white/20' : 'pt-8 border-t border-gray-200'

  return (
    <footer className={cn(onDark ? 'bg-transparent pt-10 sm:pt-12 pb-6 sm:pb-8' : 'bg-white py-12', className)}>
      <div className="max-w-6xl mx-auto px-4 sm:px-4">
        {showToolsColumns && (
          <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-8">
            {/* Services Column — first */}
            <div>
              <h3 className={headingClass}>Services</h3>
              <ul className="space-y-1">
                {servicesMenu.map((item, index) => {
                  const isExternal = item.href.startsWith('http');
                  const showDivider = index === 5; // After Agency Finder

                  return (
                    <React.Fragment key={item.href}>
                      {showDivider && <li className={dividerClass}></li>}
                      <li>
                        <Link
                          href={item.href.startsWith('http') ? item.href : toSite(item.href)}
                          className={linkClass}
                          onClick={() => {
                            trackPostHogEvent('footer_navigation', {
                              menu_item: item.name,
                              menu_category: 'Services',
                              location: 'footer',
                              href: item.href,
                              is_external: isExternal
                            });
                          }}
                        >
                          {item.name}
                        </Link>
                      </li>
                    </React.Fragment>
                  );
                })}
              </ul>
            </div>

            {/* Growth Tools Columns */}
            {productivityTools.map((categoryData) => (
              <div key={categoryData.category}>
                <h3 className={`flex items-center gap-2 ${headingClass}`}>
                  {categoryData.icon}
                  {categoryData.category}
                </h3>
                <ul className="space-y-1">
                  {categoryData.items.map((item) => (
                    <li key={item.href}>
                        <Link
                        href={item.href.startsWith('http') ? item.href : toSite(item.href)}
                        className={linkClass}
                        onClick={() => {
                          trackPostHogEvent('footer_navigation', {
                            menu_item: item.name,
                            menu_category: categoryData.category,
                            location: 'footer',
                            href: item.href
                          });
                        }}
                      >
                        {item.name}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        )}

        <div className={`text-center text-sm ${onDark ? 'text-white' : 'text-gray-600'} ${showToolsColumns ? bottomBorderClass : ''}`}>
          <div className="mb-4">
            <Link
              href={toSite('/resources')}
              className={`inline-flex items-center gap-2 ${brandClass} text-sm font-semibold tracking-[-0.01em]`}
              onClick={() => {
                trackPostHogEvent('footer_navigation', {
                  menu_item: 'Library',
                  location: 'footer',
                  href: '/resources'
                });
              }}
            >
              <Library className="h-4 w-4" strokeWidth={1.75} aria-hidden />
              Library - Free Resources
            </Link>
          </div>
          <span>
            ©{new Date().getFullYear()}&nbsp;
            <Link 
              className={brandClass}
              href={toSite('/')}
              onClick={() => {
                trackPostHogEvent('footer_navigation', {
                  menu_item: 'SmartMetrics Logo',
                  location: 'footer',
                  href: '/'
                });
              }}
            >
              SmartMetrics
            </Link>.
            &nbsp;All rights reserved.
            &nbsp;|&nbsp;
            <Link 
              href={toSite('/privacy')} 
              className={mutedClass}
              onClick={() => {
                trackPostHogEvent('footer_navigation', {
                  menu_item: 'Privacy Policy',
                  location: 'footer',
                  href: '/privacy'
                });
              }}
            >
              Privacy Policy
            </Link>
            &nbsp;|&nbsp;
            <Link 
              href={toSite('/terms')} 
              className={mutedClass}
              onClick={() => {
                trackPostHogEvent('footer_navigation', {
                  menu_item: 'Terms and Conditions',
                  location: 'footer',
                  href: '/terms'
                });
              }}
            >
              Terms and Conditions
            </Link>
            &nbsp;|&nbsp;
            <DoNotSellLink className={mutedClass} />
          </span>
        </div>
      </div>
    </footer>
  )
}
